<template>
  <div class="dashboard-container">
    <!-- 顶部数据总览卡片 -->
    <div class="stat-cards">
      <StatCard title="总用户数" :value="stats.total_users" icon="👥" bgColor="#0052cc" />
      <StatCard title="软件下载量" :value="stats.total_downloads" icon="⬇️" bgColor="#00b8d9" />
      <StatCard title="网站访问量" :value="stats.total_visits" icon="👁️" bgColor="#ff6b35" />
      <StatCard title="影像处理数" :value="stats.total_image_processing" icon="🖼️" bgColor="#8e44ad" />
    </div>

    <!-- 图表区 -->
    <div class="charts-area">
      <el-card class="chart-card">
        <template #header>
          <div class="card-header">
            <span>用户增长趋势</span>
            <el-tag type="success">最近7天</el-tag>
          </div>
        </template>
        <ChartBox
          :chart-data="userGrowthChartData"
          :chart-options="userGrowthChartOptions"
          height="400px"
        />
      </el-card>
      <el-card class="chart-card">
        <template #header>
          <div class="card-header">
            <span>下载趋势</span>
            <el-tag type="success">最近7天</el-tag>
          </div>
        </template>
        <ChartBox
          :chart-data="downloadTrendChartData"
          :chart-options="downloadTrendChartOptions"
          height="400px"
        />
      </el-card>
      <el-card class="chart-card">
        <template #header>
          <div class="card-header">
            <span>访问趋势</span>
            <el-tag type="success">最近7天</el-tag>
          </div>
        </template>
        <ChartBox
          :chart-data="visitTrendChartData"
          :chart-options="visitTrendChartOptions"
          height="400px"
        />
      </el-card>
      <el-card class="chart-card">
        <template #header>
          <div class="card-header">
            <span>影像处理趋势</span>
            <el-tag type="success">最近7天</el-tag>
          </div>
        </template>
        <ChartBox
          :chart-data="imageProcessingTrendChartData"
          :chart-options="imageProcessingTrendChartOptions"
          height="400px"
        />
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import StatCard from '@/components/admin/StatCard.vue'
import ChartBox from '@/components/admin/ChartBox.vue'
import request from '@/utils/request'
import { ElMessage } from 'element-plus'

// 统计数据
const stats = ref({
  total_users: 0,
  total_downloads: 0,
  total_visits: 0,
  total_image_processing: 0
})

// 用户增长趋势图表数据
const userGrowthChartData = ref({
  labels: [],
  datasets: [
    {
      label: '新增用户',
      data: [],
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    },
    {
      label: '累计用户',
      data: [],
      backgroundColor: 'rgba(103, 194, 58, 0.2)',
      borderColor: 'rgba(103, 194, 58, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

// 用户增长趋势图表配置
const userGrowthChartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          const label = context.dataset.label || ''
          return `${label}: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
})

// 下载趋势图表数据
const downloadTrendChartData = ref({
  labels: [],
  datasets: [
    {
      label: '下载量',
      data: [],
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

// 下载趋势图表配置
const downloadTrendChartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `下载量: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
})

// 访问趋势图表数据
const visitTrendChartData = ref({
  labels: [],
  datasets: [
    {
      label: '访问量',
      data: [],
      backgroundColor: 'rgba(255, 107, 53, 0.2)',
      borderColor: 'rgba(255, 107, 53, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

// 访问趋势图表配置
const visitTrendChartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `访问量: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
})

// 影像处理趋势图表数据
const imageProcessingTrendChartData = ref({
  labels: [],
  datasets: [
    {
      label: '影像处理量',
      data: [],
      backgroundColor: 'rgba(142, 68, 173, 0.2)',
      borderColor: 'rgba(142, 68, 173, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

// 影像处理趋势图表配置
const imageProcessingTrendChartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `影像处理量: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
})

// 获取仪表盘数据
const fetchDashboardData = async () => {
  try {
    console.log('开始获取仪表盘数据...')
    const response = await request.get('/admin/dashboard')
    console.log('获取到的数据：', response)
    
    if (!response) {
      throw new Error('返回数据格式不正确')
    }

    const { stats: statsData, userGrowth, downloadTrend, visitTrend, imageProcessingTrend } = response
    
    // 更新统计数据
    stats.value = statsData

    // 格式化日期
    const formatDate = (dateStr) => {
      const date = new Date(dateStr)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      
      // 如果是今天，显示"今天"
      if (date.toDateString() === today.toDateString()) {
        return '今天'
      }
      // 如果是昨天，显示"昨天"
      else if (date.toDateString() === yesterday.toDateString()) {
        return '昨天'
      }
      // 其他日期显示"月/日"格式
      else {
        return date.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' })
      }
    }

    // 计算累计用户数
    let cumulativeUsers = 0
    const userGrowthData = userGrowth.map(item => {
      cumulativeUsers += item.new_users
      return {
        date: item.date,
        new_users: item.new_users,
        total_users: cumulativeUsers
      }
    })

    // 更新用户增长趋势图表
    userGrowthChartData.value = {
      labels: userGrowthData.map(item => formatDate(item.date)),
      datasets: [
        {
          label: '新增用户',
          data: userGrowthData.map(item => item.new_users),
          backgroundColor: 'rgba(64, 158, 255, 0.2)',
          borderColor: 'rgba(64, 158, 255, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        },
        {
          label: '累计用户',
          data: userGrowthData.map(item => item.total_users),
          backgroundColor: 'rgba(103, 194, 58, 0.2)',
          borderColor: 'rgba(103, 194, 58, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }

    // 更新下载趋势图表
    downloadTrendChartData.value = {
      labels: downloadTrend.map(item => formatDate(item.date)),
      datasets: [
        {
          label: '下载量',
          data: downloadTrend.map(item => item.download_count),
          backgroundColor: 'rgba(64, 158, 255, 0.2)',
          borderColor: 'rgba(64, 158, 255, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }

    // 更新访问趋势图表
    visitTrendChartData.value = {
      labels: visitTrend.map(item => formatDate(item.date)),
      datasets: [
        {
          label: '访问量',
          data: visitTrend.map(item => item.visit_count),
          backgroundColor: 'rgba(255, 107, 53, 0.2)',
          borderColor: 'rgba(255, 107, 53, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }

    // 更新影像处理趋势图表
    imageProcessingTrendChartData.value = {
      labels: imageProcessingTrend.map(item => formatDate(item.date)),
      datasets: [
        {
          label: '影像处理量',
          data: imageProcessingTrend.map(item => item.processing_count),
          backgroundColor: 'rgba(142, 68, 173, 0.2)',
          borderColor: 'rgba(142, 68, 173, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }

    // 更新图表配置
    const maxNewUsers = Math.max(...userGrowthData.map(item => item.new_users))
    const maxTotalUsers = Math.max(...userGrowthData.map(item => item.total_users))
    const maxDownloads = Math.max(...downloadTrend.map(item => item.download_count))
    const maxVisits = Math.max(...visitTrend.map(item => item.visit_count))
    const maxImageProcessing = Math.max(...imageProcessingTrend.map(item => item.processing_count))

    // 设置Y轴刻度，确保能显示0值
    userGrowthChartOptions.value.scales.y.ticks.stepSize = Math.max(1, Math.ceil(Math.max(maxNewUsers, maxTotalUsers) / 5))
    downloadTrendChartOptions.value.scales.y.ticks.stepSize = Math.max(1, Math.ceil(maxDownloads / 5))
    visitTrendChartOptions.value.scales.y.ticks.stepSize = Math.max(1, Math.ceil(maxVisits / 5))
    imageProcessingTrendChartOptions.value.scales.y.ticks.stepSize = Math.max(1, Math.ceil(maxImageProcessing / 5))

    console.log('图表数据更新完成')
  } catch (error) {
    console.error('获取仪表盘数据失败:', error)
    ElMessage.error(error.response?.data?.message || '获取仪表盘数据失败')
  }
}

// 初始化
onMounted(() => {
  fetchDashboardData()
})
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
}

.stat-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.charts-area {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 20px;
}

.chart-card {
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
}

:deep(.el-card__header) {
  padding: 15px 20px;
  border-bottom: 1px solid #ebeef5;
  background-color: #f5f7fa;
}

:deep(.el-card__body) {
  padding: 20px;
}
</style>